import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import { BookingWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardData/BaseBookingWizardData';

/**
 * Why this sailing cannot go with the booking's other leg, or null where it can.
 *
 * <p>The one rule that applies to everyone, managers included: a departure cannot leave at or after
 * the return, a return cannot leave at or before the departure, and neither can be the other leg's
 * own sailing. Read by `useIsLegSelectable` to refuse the sailing, by `useLegErrorMessage` to say
 * why, and by the fare card to fold a sailing away that could never be taken.</p>
 */
export type LegClash = 'SAME_TRIP' | 'AFTER_RETURN' | 'BEFORE_DEPARTURE';

export const legClash = (
	trip: TicketsTabTrip,
	wizardData: BookingWizardData,
	legType: 'departure' | 'return',
): LegClash | null => {
	if (legType === 'departure') {
		if (
			wizardData.returningJourney?.tripId === trip.id &&
			wizardData.returningJourney?.startStopId === trip.startStopId &&
			wizardData.returningJourney?.endStopId === trip.endStopId
		) {
			return 'SAME_TRIP';
		}
		if (
			wizardData.returningJourney?.departureDateTime &&
			trip.departureDateTime >= wizardData.returningJourney.departureDateTime &&
			trip.id !== wizardData.returningJourney.tripId
		) {
			return 'AFTER_RETURN';
		}
	}

	if (legType === 'return') {
		if (
			wizardData.departureTrip !== false && // altering only return leg of two way booking uses departing journey
			wizardData.departingJourney?.tripId === trip.id &&
			wizardData.departingJourney?.startStopId === trip.startStopId &&
			wizardData.departingJourney?.endStopId === trip.endStopId
		) {
			return 'SAME_TRIP';
		}
		if (
			// When only the return leg is being altered, `departingJourney` holds THAT leg's own trip,
			// not a departure to sit after. Left off, this refused every trip at or before the leg's
			// current time — so the leg could only ever be moved later, and a customer trying to move
			// it earlier clicked a dead option and reached the end of the wizard with nothing changed.
			wizardData.departureTrip !== false &&
			wizardData.departingJourney?.departureDateTime &&
			trip.departureDateTime <= wizardData.departingJourney.departureDateTime &&
			trip.id !== wizardData.departingJourney.tripId
		) {
			return 'BEFORE_DEPARTURE';
		}
	}

	return null;
};
